import { useCallback, useState } from 'react';
import { BookOpen, Loader2 } from 'lucide-react';

import { useAuth } from '@/hooks/auth/useAuth';
import { useEffectiveSocietyId } from '@/features/warranties/hooks/useEffectiveSocietyId';
import { usePresaleSheetConfig } from '../hooks/usePresaleSheetConfig';
import { PresaleSheetEditor } from '../components/PresaleSheetEditor';
import { PresaleSheetPreview } from '../components/PresaleSheetPreview';
import { AutocompleteSelect } from '@/Components/shared/AutocompleteSelect';
import type { WarrantyListItem } from '@/features/warranties/types/warranty.types';
import type { SocietyListItem } from '@/features/societies/types/society.types';

export default function PresaleSheetPage() {
  const { user } = useAuth();
  const effectiveSocietyId = useEffectiveSocietyId(user?.societyId);
  const isInternalUser = user?.type === 'INTERNAL';
  const [selectedSocietyId, setSelectedSocietyId] = useState<string | null>(null);
  const [selectedWarrantyUuid, setSelectedWarrantyUuid] = useState<string | null>(null);
  const [selectedWarrantyName, setSelectedWarrantyName] = useState<string | null>(null);

  const activeSocietyId = isInternalUser ? selectedSocietyId : effectiveSocietyId;

  const { data: persistedConfig, isLoading: configLoading } = usePresaleSheetConfig(selectedWarrantyUuid);

  const transformResponse = useCallback((data: any) => ({
    nodes: data.warranties || [],
    pageInfos: {
      hasNextPage: data.page < data.totalPages - 1,
      nextPage: data.page,
    },
  }), []);

  return (
    <div className="tw-mx-auto tw-p-3 sm:tw-p-4 md:tw-p-6">
      <style>{`.react-pdf__Page__canvas { max-width: 100% !important; height: auto !important; }`}</style>

      <div className="tw-flex tw-items-center tw-gap-3 tw-mb-6">
        <BookOpen className="tw-w-6 tw-h-6 tw-text-affinicar-primary" />
        <h1 className="tw-text-lg sm:tw-text-xl tw-font-bold tw-text-gray-900 tw-m-0">Brochure avant vente</h1>
      </div>

      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-6">
        {/* Colonne gauche : Sélection */}
        <div>
          {/* Sélection société (internal user uniquement) */}
          {isInternalUser && (
            <div className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-4 sm:tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4 tw-mb-4">
              <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
                Sélectionnez la société pour laquelle générer la brochure
              </p>
              <AutocompleteSelect<SocietyListItem>
                apiEndpoint="/societies"
                searchParam="nameFilter"
                getOptionLabel={(s) => s.name}
                getOptionValue={(s) => s.uuid}
                value={selectedSocietyId}
                onChange={(uuid) => {
                  setSelectedSocietyId(uuid);
                  setSelectedWarrantyUuid(null);
                  setSelectedWarrantyName(null);
                }}
                label="Société"
                required
                placeholder="Rechercher une société..."
                filterActive
                renderOption={(s) => (
                  <div>
                    <div className="tw-font-medium">{s.name}</div>
                    {s.rootParentName && (
                      <div className="tw-text-xs tw-text-gray-500">Groupe : {s.rootParentName}</div>
                    )}
                  </div>
                )}
              />
            </div>
          )}

          {/* Sélection garantie */}
          <div className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-4 sm:tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4">
            <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
              Sélectionnez une garantie pour générer la brochure avant vente
            </p>
            <AutocompleteSelect<WarrantyListItem>
              apiEndpoint="/warranties-admin"
              searchParam="search"
              getOptionLabel={(w) => `${w.name} — ${w.duration} mois`}
              getOptionValue={(w) => w.uuid}
              value={selectedWarrantyUuid}
              onChange={(uuid, item) => {
                setSelectedWarrantyUuid(uuid);
                setSelectedWarrantyName(item?.name || null);
              }}
              label="Garantie"
              required
              placeholder={activeSocietyId ? 'Rechercher une garantie...' : 'Sélectionnez d\'abord une société'}
              disabled={isInternalUser && !activeSocietyId}
              additionalParams={{
                societyId: activeSocietyId || undefined,
                isActive: true,
                isBroadcast: true,
              }}
              transformResponse={transformResponse}
              renderOption={(w) => (
                <div>
                  <div className="tw-font-medium">{w.name}</div>
                  <div className="tw-text-xs tw-text-gray-500">{w.duration} mois</div>
                </div>
              )}
            />
          </div>

          {selectedWarrantyUuid && configLoading && (
            <div className="tw-mt-4 tw-flex tw-items-center tw-justify-center tw-py-6">
              <Loader2 className="tw-w-5 tw-h-5 tw-animate-spin tw-text-gray-400" />
              <span className="tw-ml-2 tw-text-sm tw-text-gray-500">Chargement de la configuration...</span>
            </div>
          )}

          {selectedWarrantyUuid && persistedConfig && !configLoading && (
            <PresaleSheetEditor
              key={selectedWarrantyUuid}
              warrantyUuid={selectedWarrantyUuid}
              initialConfig={persistedConfig}
              variant="page"
            />
          )}
        </div>

        {/* Colonne droite : Preview PDF */}
        <div className="tw-order-2 lg:tw-order-none tw-max-w-md tw-mx-auto lg:tw-max-w-none">
          {selectedWarrantyUuid && persistedConfig && !configLoading && (
            <PresaleSheetPreview
              key={selectedWarrantyUuid}
              warrantyUuid={selectedWarrantyUuid}
              warrantyName={selectedWarrantyName}
              initialConfig={persistedConfig}
            />
          )}
        </div>
      </div>
    </div>
  );
}
